import { useMember } from "./useMember";
import { usePolicy } from "./usePolicy";
import { Relationship } from "../component/enums/Relationship";
import {
    AccidentPolicyCoverage,
    AdministrationType, CriticalIllnessPolicyCoverage,
    GroupTermLifePolicyCoverage,
    MemberCoverages,
    MemberGroupCoverages,
    VoluntaryTermLifePolicyCoverage
} from "../generated/graphql";
import _ from "lodash";
import { AuthContextProps } from "oidc-react";
import { QueryHookOptions, QueryResult } from "@apollo/client/react/types/types";
import { GenericRelationship } from "../component/enums/GenericRelationship";

export function useCoverages(policyId?: string, memberId?: string, relationship?: Relationship | GenericRelationship, effectiveDate?: string, auth?: AuthContextProps, options?: QueryHookOptions): QueryResult<{ findCoverages: MemberGroupCoverages | MemberCoverages | null }> {
    const { data: memberData, loading: memberLoading, error: memberError } = useMember(policyId, memberId, effectiveDate, auth, options);
    const member = memberData?.findMember;
    const { data: policyData, loading: policyLoading, error: policyError } = usePolicy(policyId, memberId, effectiveDate, auth, options)
    const policy = policyData?.findPolicy;

    if(memberLoading || policyLoading) {
        return {
            data: {
                findCoverages: null
            },
            loading: true,
            error: memberError || policyError
        } as QueryResult
    }

    const isCoveredDependent = [Relationship.COVERED_DEPENDENT, Relationship.FAMILY_MEMBER, GenericRelationship.FAMILY_MEMBER_OR_COVERED_DEPENDENT].includes(relationship ?? Relationship.EMPLOYEE)

    const groupCoverages = policy?.memberGroups?.[0]?.coverages;
    const dependentGroupCoverages = groupCoverages
        ? Object.fromEntries(Object.entries(groupCoverages).filter(([_, value]) => {
            const groupTermLife = value as GroupTermLifePolicyCoverage;
            const voluntaryTermLife = value as VoluntaryTermLifePolicyCoverage;
            const accident = value as AccidentPolicyCoverage;
            const criticalIllness = value as CriticalIllnessPolicyCoverage
            return groupTermLife?.dependent || voluntaryTermLife?.spouse || voluntaryTermLife?.child
                || accident?.dependent || criticalIllness?.spouse || criticalIllness?.child
        }))
        : {}
    const selfAccountingCoverages = isCoveredDependent ? dependentGroupCoverages : groupCoverages
    const memberCoverages = member?.coverages;
    const dependentMemberCoverages = member?.dependents
        ?.map(dependent => dependent.coverages)
        ?.reduce((accumulator, current) => {
            const truthyFields = Object.fromEntries(Object.entries(current).filter(([_, value]) => !!value))
            return _.merge(accumulator, truthyFields)
        }, {});
    const standardAccountingCoverages = (isCoveredDependent ? dependentMemberCoverages : memberCoverages) as MemberCoverages

    const isSelfAccounting = policy?.administrationType === AdministrationType.SelfAccounting;
    let coverages = isSelfAccounting ? selfAccountingCoverages : standardAccountingCoverages
    if(groupCoverages?.paidFamilyLeave || groupCoverages?.paidMedicalLeave) {
        const clonedCoverages = _.cloneDeep(coverages) as MemberGroupCoverages;
        clonedCoverages.paidFamilyLeave = groupCoverages.paidFamilyLeave;
        clonedCoverages.paidMedicalLeave = groupCoverages.paidMedicalLeave;
        coverages = clonedCoverages;
    }

    return { data: { findCoverages: coverages }, loading: memberLoading || policyLoading, error: memberError || policyError } as QueryResult
}